import {
  type PropsWithChildren,
  type ReactNode,
  Suspense,
  createContext,
  useContext,
} from 'react';

import type { Value } from '@spotify-confidence/sdk';

import {
  type FlagEvaluationAttributes,
  type FlagValueResolver,
  interceptEvaluationContext,
  interceptFlagValue,
} from '../../interceptors';
import {
  OpenFeatureBoundary,
  setEvaluationContext,
  useOpenFeatureFlagValue,
} from './vendor';

const useDefaultFlagValue = <T extends Value>(
  _flag: string,
  defaultValue: T,
): T => defaultValue;

const FlagResolverContext =
  createContext<FlagValueResolver>(useDefaultFlagValue);

export const FlagResolverProvider = ({
  resolver,
  children,
}: PropsWithChildren<{
  resolver: FlagValueResolver;
}>) => (
  <FlagResolverContext.Provider value={resolver}>
    {children}
  </FlagResolverContext.Provider>
);

export const useFlagResolver = (): FlagValueResolver =>
  useContext(FlagResolverContext);

const useFlagValue = interceptFlagValue(useOpenFeatureFlagValue);

export const FlagEvaluationProvider = ({
  children,
  fallback,
}: PropsWithChildren<{
  fallback: ReactNode;
}>) => (
  <OpenFeatureBoundary>
    <FlagResolverProvider resolver={useFlagValue}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </FlagResolverProvider>
  </OpenFeatureBoundary>
);

export const setOpenFeatureFlagEvaluationContext: (
  attributes: FlagEvaluationAttributes,
) => void = interceptEvaluationContext(setEvaluationContext);
